








































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { Button, Card, Tooltip } from 'element-ui';
import { utcToLocal } from '@/utilities/dateCast.ts';
import { milestoneService } from '@/services/MilestoneService';
import { getMilestoneType } from '@/utilities/textUtils.ts';

import Status from '@/components/shipment/activity/Status.vue';

// types
import { ReservationStatusClasses } from '@/constants/reservationStatuses';
import {
  ShipmentFields,
  MilestoneFields,
  MilestoneTypeEnum,
  DevanningDetailsInput,
  MilestoneDoneDateEnum
} from '@/types/graphql/schemaTypes';

import { LocaleMessages } from 'vue-i18n';
import get from 'lodash/get';

interface FormatedMilestone extends MilestoneFields {
  type?: string | LocaleMessages;
  deliveryIndex?: number;
  eventName: MilestoneDoneDateEnum;
  doneAt: Date | null;
  hidden?: boolean;
  drayageReserved?: boolean;
  drayageReservation?: ReservationStatusClasses;
  totalDrayage?: boolean;
}

interface Container {
  containerNumber: string | null;
  containerReference: string | null;
  deliveryPlace: string | null;
  milestones: FormatedMilestone[] | null;
}

@Component({
  components: {
    Status,
    'el-button': Button,
    'el-card': Card,
    'el-tooltip': Tooltip
  }
})
export default class Milestones extends Vue {
  @Prop({ required: true, type: Object }) private shipment!: ShipmentFields;
  @Prop({ required: true, type: Object }) private shipmentStatus;

  private containers: Container[] = [];

  // goupe of milestone
  private dateTimeTypes = [
    MilestoneTypeEnum.delivery,
    MilestoneTypeEnum.pickup
  ];

  private readonly drayageTypes = [
    MilestoneTypeEnum.delivery
  ];

  private transportType = [
    MilestoneTypeEnum.air_transportation,
    MilestoneTypeEnum.sea_transportation
  ];

  created() {
    this.formatMiletones(this.shipment.milestones);
  }

  refresh() {
    this.$emit('refresh');
  }

  private isDrayage(milestone, containerIndex) {
    if (milestone.drayageReservation) {
      return true;
    }
    if (this.drayageTypes.includes(milestone.milestoneType)) {
      return containerIndex === 0;
    }
  }

  private getDrayageClass(milestone, containerIndex, milestoneIndex?) {
    // This reflects all sub-drayages value
    if (milestone.totalDrayage) {
      const containersLength = milestone.devanningDetails?.container?.deliveryPlaces?.length;
      let reserved = 0;
      // starts at 1 because of main container line
      for (let i = 1; i < this.containers?.length; i++) {
        if (this.containers[i]?.milestones?.[milestoneIndex]?.drayageReserved) {
          reserved++;
        }
      }
      if (reserved === containersLength) {
        // Complete
        return ReservationStatusClasses.complete;
      } else if (reserved > 0) {
        // Partial
        return ReservationStatusClasses.partial;
      } else {
        // None
        return ReservationStatusClasses.none;
      }
    }
    if (milestone.drayageReservation) {
      return milestone.drayageReservation;
    }
    // The first raw doesn't have the drayageReservation computed property
    if (containerIndex === 0) {
      return milestone.details.drayageReserved ? ReservationStatusClasses.complete
        : ReservationStatusClasses.none;
    }
  }

  private milestoneStatusClass(className: string, complete: boolean | null, deadLine: string) {
    if (complete) {
      return className + '-done';
    }
    if (!deadLine) {
      return;
    }
    if (new Date(utcToLocal(deadLine)) < new Date()) {
      return className + '-late';
    }
  }

  private findMilestoneDoneAt(milestone: MilestoneFields, eventName: MilestoneDoneDateEnum) {
    const doneDate = milestone.doneDates.find((date) => {
      return date.eventName === eventName;
    });

    return doneDate ? doneDate.doneAt || null : null;
  }

  private async checkMilestoneStep(milestone: FormatedMilestone) {
    try {
      if (typeof (milestone.deliveryIndex) === 'number') {
        await this.checkDevanningPlace(milestone, milestone.deliveryIndex);
      } else {
        await this.checkMilestone(milestone);
      }

      this.$store.dispatch('Alert/addAlert', {
        type: 'success',
        message: this.$t('success.updated'),
        time: 1000
      });
    } catch (e) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    }
  }

  private async checkDevanningPlace(milestone: FormatedMilestone, deliveryPlaceIndex: number) {
    const deliveryPlaces = milestone.devanningDetails!.container!.deliveryPlaces;
    milestone.doneAt = (milestone.doneAt) ? null : new Date();
    deliveryPlaces![deliveryPlaceIndex].doneAt = milestone.doneAt;

    const m = await milestoneService.updateMilestone({ devanningDetails: milestone.devanningDetails },
      milestone.id);
    return m;
  }

  private async checkMilestone(milestone: FormatedMilestone) {
    const response = await milestoneService.checkMilestone({
      id: milestone.id,
      done: !milestone.doneAt,
      eventName: milestone.eventName
    });
    const checkedMilestone = response.data && response.data.checkMilestone;

    if (checkedMilestone) {
      // find and update all corresponding milestones
      this.containers.forEach((container) => {
        const indexes: number[] = container.milestones!.reduce((arr: number[], m, i) => {
          if (m && m.id === milestone.id) {
            arr.push(i);
          }
          return arr;
        }, []);

        //  update milestone(s) with server return value
        indexes.forEach((index: number) => {
          const formatedMilestone: FormatedMilestone = container.milestones![index];

          if (typeof (formatedMilestone.deliveryIndex) !== 'number') {
            formatedMilestone.doneAt = this.findMilestoneDoneAt(checkedMilestone as MilestoneFields,
              formatedMilestone.eventName);
          }
        });
      });
    }
  }

  private formatDevanning(
    currentContainer: Container,
    milestone: MilestoneFields,
    maxLength: number
  ) {
    // delivery places management
    const places = get(milestone, 'devanningDetails.container.deliveryPlaces');

    if (places.length) {
      places.forEach((place: any, index: number) => {
        // add to main timeline
        if (!index) {
          // add devanning
          const drayageReserved = milestone.details?.drayageReserved;
          currentContainer.milestones!.push({
            ...milestone,
            eventDate: get(milestone, 'devanningDate'),
            eventName: MilestoneDoneDateEnum.doneAt,
            doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.doneAt),
            type: this.$i18n.t('shipment.milestone.short-milestone-type.devanning')
          });
          // add delivery
          currentContainer.milestones!.push({
            ...milestone,
            milestoneType: MilestoneTypeEnum.delivery,
            hidden: true,
            eventName: MilestoneDoneDateEnum.doneAt,
            doneAt: get(
              milestone,
              `devanningDetails.container.deliveryPlaces[${index}].doneAt`,
              null
            ),
            eventDate: get(
              milestone,
              'devanningDetails.container.deliveryPlaces[0].deliveryDate',
              null
            ),
            type: this.$i18n.t(`milestoneType.${MilestoneTypeEnum.delivery}`),
            drayageReserved,
            totalDrayage: true
          });
        }

        // add timeline for the devanning
        this.containers.push({
          containerNumber: get(
            milestone,
            'devanningDetails.container.containerNumber',
            null
          ),
          containerReference: get(
            milestone,
            'devanningDetails.container.containerReference',
            null
          ),
          deliveryPlace: place.deliveryPlace,
          milestones: Array(maxLength).fill(null)
        });

        // add devanning
        const drayageReserved = get(
          milestone,
          `devanningDetails.container.deliveryPlaces[${index}].drayageReserved`,
          null
        );
        this.containers[this.containers.length - 1].milestones!.push({
          ...milestone,
          eventName: MilestoneDoneDateEnum.doneAt,
          doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.doneAt),
          eventDate: get(milestone, 'devanningDate'),
          type: this.$i18n.t('shipment.milestone.short-milestone-type.devanning')
        });
        // add delivery
        this.containers[this.containers.length - 1].milestones!.push({
          ...milestone,
          doneAt: get(
            milestone,
            `devanningDetails.container.deliveryPlaces[${index}].doneAt`,
            null
          ),
          deliveryIndex: index,
          eventName: MilestoneDoneDateEnum.doneAt,
          eventDate: get(
            milestone,
            `devanningDetails.container.deliveryPlaces[${index}].deliveryDate`,
            null
          ),
          milestoneType: MilestoneTypeEnum.delivery,
          type: this.$i18n.t(`milestoneType.${MilestoneTypeEnum.delivery}`),
          drayageReserved,
          drayageReservation: drayageReserved ? ReservationStatusClasses.complete
            : ReservationStatusClasses.none
        });
      });
    // if milestones doesn't have delivery place => add simple devanning
    } else {
      // add to main timeline
      currentContainer.milestones!.push({
        ...milestone,
        eventDate: get(milestone, 'devanningDate'),
        eventName: MilestoneDoneDateEnum.doneAt,
        doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.doneAt),
        type: this.$i18n.t('shipment.milestone.short-milestone-type.devanning')
      });
      // add new container
      this.containers.push({
        containerNumber: get(
          milestone,
          'devanningDetails.container.containerNumber',
          null
        ),
        containerReference: get(
          milestone,
          'devanningDetails.container.containerReference',
          null
        ),
        deliveryPlace: null,
        milestones: Array(maxLength).fill(null)
      });
      this.containers[this.containers.length - 1].milestones!.push({
        ...milestone,
        eventDate: get(milestone, 'devanningDate'),
        eventName: MilestoneDoneDateEnum.doneAt,
        doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.doneAt),
        type: this.$i18n.t('shipment.milestone.short-milestone-type.devanning')
      });
      return 1;
    }
    return 2;
  }

  private formatTransportation(currentContainer: Container, milestone: MilestoneFields) {
    const i18nKey = 'shipment.milestone.short-milestone-type';
    // departure
    currentContainer.milestones!.push({
      ...milestone,
      eventDate: milestone.estimatedDateOfDeparture,
      eventName: MilestoneDoneDateEnum.etdDoneAt,
      doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.etdDoneAt),
      type: this.$i18n.t(`${i18nKey}.${milestone.milestoneType}`) + ' ETD'
    });
    // arrival
    currentContainer.milestones!.push({
      ...milestone,
      eventDate: milestone.estimatedDateOfArrival,
      eventName: MilestoneDoneDateEnum.etaDoneAt,
      doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.etaDoneAt),
      type: this.$i18n.t(`${i18nKey}.${milestone.milestoneType}`) + ' ETA'
    });
  }

  private formatMiletones(source: MilestoneFields[] | null) {
    if (!source) {
      return;
    }

    this.containers = [{
      containerNumber: null,
      containerReference: null,
      deliveryPlace: null,
      milestones: []
    }];

    const currentContainer = this.containers[0];
    let maxLength = 0;

    source.forEach((milestone, index) => {
    // split air and sea milestone
      if (this.transportType.includes(milestone.milestoneType!)) {
        this.formatTransportation(currentContainer, milestone);
        // add 2 to maxLength
        maxLength += 2;
        // jump to next index
        return;
      }

      // split devanning
      if (milestone.milestoneType === MilestoneTypeEnum.devanning) {
        maxLength += this.formatDevanning(currentContainer, milestone, maxLength);
        // add 2 to maxLength
        // jump to next index
        return;
      }

      // add simple milestone
      currentContainer.milestones!.push({
        ...milestone,
        eventName: MilestoneDoneDateEnum.doneAt,
        doneAt: this.findMilestoneDoneAt(milestone, MilestoneDoneDateEnum.doneAt),
        type: getMilestoneType(milestone)
      });
      maxLength += 1;
    });

    //  make all array same size for the html table
    this.containers.forEach((container, index) => {
      if (container.milestones!.length < maxLength) {
        const diff = (maxLength - container.milestones!.length) || 0;
        for (let index = 0; index < diff; index += 1) {
          container.milestones!.push();
        }
      }
    });
  }
}
